import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Bot, Send, Loader2, ThumbsUp, ThumbsDown, FileText, Video, PhoneCall,
  AlertTriangle, Clock, Sparkles, User, Plus, History, MessageSquare,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { TypeBadge } from "@/components/Common";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const SUGGESTIONS = {
  zap91: ["How do I create and print a shipment label?", "What documents are required for a shipment?", "Barcode won't scan — what do I do?"],
  ewms: ["How do I perform putaway?", "Where do I assign a bin location?", "Pallet scan fails — what next?"],
  bill_booking: ["Where do I upload the invoice?", "How do I book a vendor invoice?", "The 3-way match failed — what should I do next?"],
};

const MD_COMPONENTS = {
  h1: (p) => <h3 className="font-display text-xl mt-4 mb-2 leading-tight" {...p} />,
  h2: (p) => <h3 className="font-display text-lg mt-4 mb-2 leading-tight" {...p} />,
  h3: (p) => <h4 className="font-display text-base mt-3 mb-1.5 leading-tight" {...p} />,
  h4: (p) => <h5 className="font-semibold text-sm mt-3 mb-1" {...p} />,
  p:  (p) => <p className="my-2 leading-relaxed" {...p} />,
  ul: (p) => <ul className="my-2 pl-5 space-y-1 list-disc marker:text-primary" {...p} />,
  ol: (p) => <ol className="my-2 pl-5 space-y-1 list-decimal marker:text-primary marker:font-mono" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  strong: (p) => <strong className="font-semibold text-foreground" {...p} />,
  em: (p) => <em className="italic" {...p} />,
  code: ({ inline, ...p }) => inline
    ? <code className="font-mono text-[0.85em] bg-background/60 rounded px-1 py-0.5 border border-border" {...p} />
    : <code className="font-mono text-[0.85em] block bg-background/60 rounded p-3 border border-border overflow-x-auto" {...p} />,
  hr: () => <div className="kp-rule my-4" />,
  a: (p) => <a className="text-primary kp-link" target="_blank" rel="noreferrer" {...p} />,
  table: (p) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm" {...p} />
    </div>
  ),
  thead: (p) => <thead className="bg-secondary/50" {...p} />,
  th: (p) => <th className="text-left px-3 py-2 font-semibold text-foreground border-b border-border text-xs uppercase tracking-wider" {...p} />,
  td: (p) => <td className="px-3 py-2 border-b border-border/50 align-top" {...p} />,
  blockquote: (p) => <blockquote className="my-3 pl-3 border-l-2 border-primary/40 italic text-foreground/80" {...p} />,
};

function AnswerMarkdown({ text }) {
  return (
    <div className="font-editorial [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

export default function Chat() {
  const { activeProgram } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [escOpen, setEscOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);
  const location = useLocation();
  const askedInitial = useRef(false);

  const loadSessions = () => api.get("/chat/sessions").then((r) => setSessions(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/escalations", { params: { program: activeProgram } }).then((r) => setEscalations(r.data)).catch(() => {});
    loadSessions();
  }, [activeProgram]);

  const openSession = async (sid) => {
    try {
      const { data } = await api.get("/chat/history", { params: { session_id: sid } });
      setSessionId(sid);
      setMessages(data.map((m) => ({
        role: m.role, text: m.text, sources: m.sources || [], suggested: m.suggested || [],
        answered: m.answered, message_id: m.role === "assistant" ? m.id : undefined, feedback: m.feedback || null,
      })));
    } catch {/**/}
  };

  const newChat = () => { setSessionId(null); setMessages([]); };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (location.state?.q && !askedInitial.current && activeProgram) {
      askedInitial.current = true;
      ask(location.state.q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, activeProgram]);

  const ask = async (text) => {
    if (!text.trim() || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: text, program_id: activeProgram, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, {
        role: "assistant", text: data.answer, sources: data.sources, suggested: data.suggested,
        answered: data.answered, message_id: data.message_id, feedback: null,
      }]);
      loadSessions();
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Something went wrong. Please try again or raise an escalation.", answered: false, sources: [], suggested: [] }]);
    }
    setLoading(false);
  };

  const sendFeedback = async (idx, rating) => {
    const msg = messages[idx];
    if (!msg.message_id) return;
    setMessages((m) => m.map((x, i) => (i === idx ? { ...x, feedback: rating } : x)));
    try { await api.post("/chat/feedback", { message_id: msg.message_id, rating }); toast.success("Thanks for your feedback"); }
    catch {/**/}
  };

  return (
    <div className="flex gap-6 h-[calc(100vh-9rem)] kp-in">
      {/* History sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 rounded-2xl border border-border bg-card overflow-hidden">
        <div className="p-3 border-b border-border">
          <Button className="w-full rounded-full" size="sm" onClick={newChat} data-testid="new-chat-button">
            <Plus className="w-4 h-4 mr-1.5" /> New chat
          </Button>
        </div>
        <div className="px-3 py-2.5 text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1.5">
          <History className="w-3 h-3" /> History
        </div>
        <div className="flex-1 overflow-y-auto kp-scroll px-2 pb-2 space-y-1" data-testid="chat-history">
          {sessions.length === 0 && <p className="text-xs text-muted-foreground px-2 py-4 italic">No past chats yet.</p>}
          {sessions.map((s) => (
            <button key={s.session_id} onClick={() => openSession(s.session_id)} data-testid={`history-${s.session_id}`}
              className={`w-full text-left rounded-lg px-3 py-2 text-sm transition truncate ${
                sessionId === s.session_id ? "bg-secondary/70 text-primary font-medium" : "text-foreground/70 hover:bg-secondary/40"}`}>
              {s.title || "Conversation"}
            </button>
          ))}
        </div>
      </aside>

      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-primary mb-1 flex items-center gap-1.5">
              <MessageSquare className="w-3 h-3" /> AI Process Assistant
            </p>
            <h1 className="font-display text-3xl md:text-4xl text-foreground leading-tight">Grounded answers, <span className="italic text-primary">step by step</span>.</h1>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">Based on your SOPs, workflows and FAQs — with sources, never guesses.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setEscOpen(true)} data-testid="escalation-trigger-button" className="rounded-full">
            <PhoneCall className="w-4 h-4 mr-1.5" /> Support
          </Button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto kp-scroll rounded-2xl border border-border bg-card p-5 md:p-7 space-y-6" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-secondary/60 flex items-center justify-center mb-5">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <h2 className="font-display text-2xl text-foreground">What do you need help with?</h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-md leading-relaxed">
                Ask in plain language. I'll guide you step by step — where to go, what to enter,
                what's mandatory, and what to do next.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-2 max-w-xl">
                {(SUGGESTIONS[activeProgram] || []).map((s) => (
                  <button key={s} onClick={() => ask(s)} data-testid="chat-suggestion"
                    className="text-sm rounded-full border border-border bg-background px-4 py-2 text-foreground/70 hover:border-primary/40 hover:text-primary hover:bg-secondary/40 transition-all">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 kp-up ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && (
                <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center shrink-0">
                  <Bot className="w-4 h-4 text-primary-foreground" />
                </div>
              )}
              <div className={`max-w-[85%] md:max-w-[75%] ${m.role === "user" ? "order-1" : ""}`}>
                <div className={`rounded-2xl px-5 py-3.5 text-[15px] leading-relaxed ${
                  m.role === "user" ? "bg-primary text-primary-foreground rounded-tr-md"
                  : m.answered === false ? "bg-[#fdf2c9] border border-[#f0d98c] text-[#5c4310] rounded-tl-md"
                  : "bg-secondary/50 border border-secondary text-foreground rounded-tl-md"}`}>
                  {m.answered === false && (
                    <div className="flex items-center gap-1.5 text-[#7a5a12] font-semibold mb-2 text-sm">
                      <AlertTriangle className="w-4 h-4" /> Not in the knowledge base
                    </div>
                  )}
                  <div className="text-[15px] leading-relaxed"><AnswerMarkdown text={m.text} /></div>
                </div>

                {m.suggested?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {m.suggested.map((s) => (
                      <a key={s.id} href={s.url || "#"} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs rounded-full border border-[#a5dbc0] bg-[#d9f2e5] px-3 py-1.5 text-[#25604d] hover:opacity-90 transition font-medium">
                        <Video className="w-3.5 h-3.5" /> {s.title}
                      </a>
                    ))}
                  </div>
                )}

                {m.sources?.length > 0 && (
                  <div className="mt-3 rounded-xl border border-border bg-card p-3.5">
                    <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-2 flex items-center gap-1.5">
                      <FileText className="w-3 h-3" /> Sources
                    </p>
                    <div className="space-y-1.5">
                      {m.sources.map((s) => (
                        <div key={s.id} className="flex items-center gap-2 text-xs">
                          <TypeBadge type={s.type} />
                          <span className="font-medium text-foreground truncate">{s.title}</span>
                          <span className="font-mono text-muted-foreground">{s.source}</span>
                          <span className="ml-auto text-muted-foreground flex items-center gap-1 shrink-0">
                            <Clock className="w-3 h-3" /> {s.last_updated}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {m.role === "assistant" && m.message_id && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground">Was this helpful?</span>
                    <button onClick={() => sendFeedback(i, "up")} data-testid="feedback-thumbs-up-button"
                      className={`p-1 rounded transition ${m.feedback === "up" ? "text-[#25604d]" : "text-muted-foreground/50 hover:text-[#25604d]"}`}>
                      <ThumbsUp className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => sendFeedback(i, "down")} data-testid="feedback-thumbs-down-button"
                      className={`p-1 rounded transition ${m.feedback === "down" ? "text-destructive" : "text-muted-foreground/50 hover:text-destructive"}`}>
                      <ThumbsDown className="w-3.5 h-3.5" />
                    </button>
                    {m.answered === false && (
                      <button onClick={() => setEscOpen(true)} className="text-[11px] text-primary font-semibold hover:underline ml-1">
                        Contact support →
                      </button>
                    )}
                  </div>
                )}
              </div>
              {m.role === "user" && (
                <div className="w-8 h-8 rounded-xl bg-foreground flex items-center justify-center shrink-0 order-2">
                  <User className="w-4 h-4 text-background" />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex gap-3 kp-fade">
              <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center shrink-0">
                <Bot className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="rounded-2xl rounded-tl-md bg-secondary/50 border border-secondary px-5 py-3 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Thinking through the steps…
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="mt-4 rounded-2xl border border-border bg-card p-2 flex items-end gap-2 focus-within:border-primary/40 focus-within:ring-1 focus-within:ring-primary/20 transition">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(input); } }}
            placeholder="Ask about a process… e.g. 'What should I do next after uploading the invoice?'"
            data-testid="ai-chat-input"
            rows={1}
            className="flex-1 resize-none border-0 focus-visible:ring-0 shadow-none min-h-[44px] max-h-32 bg-transparent"
          />
          <Button onClick={() => ask(input)} disabled={loading || !input.trim()}
            data-testid="ai-chat-submit-button" size="icon"
            className="shrink-0 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <Dialog open={escOpen} onOpenChange={setEscOpen}>
        <DialogContent data-testid="escalation-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <PhoneCall className="w-5 h-5 text-primary" /> Support
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {escalations.map((e) => (
              <div key={e.id} className="rounded-xl border border-border p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">{e.type}</span>
                  <span className="text-[10px] font-mono rounded-full bg-muted px-2 py-0.5 text-muted-foreground uppercase tracking-wider">SLA {e.sla}</span>
                </div>
                <p className="text-sm text-foreground/80 mt-1">{e.name}</p>
                <a href={`mailto:${e.email}`} className="text-sm text-primary kp-link">{e.email}</a>
                <p className="text-xs text-muted-foreground mt-1">{e.note}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
